import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Lookup, Modal, Notice, Panel, Table, type ColumnDef,
} from '../design/index.js'
import { useApi, type ScheduleTask } from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDate } from '../framework/format.js'
import { SheetImportDialog } from '../framework/SheetImport.js'

/**
 * The project schedule (docs/20 D1).
 *
 * Three sets of dates side by side, because that is the whole point: what was
 * agreed, what is now expected, and what happened. A screen that shows only
 * the first is a plan; a screen that shows only the last is a diary. The gap
 * between the first two is what a client meeting is about.
 *
 * Deliberately a table, not a Gantt chart. A bar chart of four hundred tasks
 * is a picture; a sorted table of the twelve that are late is a decision.
 */

interface Draft {
  id?: string
  wbs: string
  name: string
  plannedStart: string
  plannedFinish: string
  forecastStart: string
  forecastFinish: string
  actualStart: string
  actualFinish: string
  percentComplete: string
  employeeId: string
  remarks: string
}

const blank = (): Draft => ({
  wbs: '', name: '',
  plannedStart: '', plannedFinish: '',
  forecastStart: '', forecastFinish: '',
  actualStart: '', actualFinish: '',
  percentComplete: '0', employeeId: '', remarks: '',
})

const draftOf = (task: ScheduleTask): Draft => ({
  id: task.id,
  wbs: task.wbs ?? '',
  name: task.name,
  plannedStart: task.plannedStart ?? '',
  plannedFinish: task.plannedFinish ?? '',
  forecastStart: task.forecastStart ?? '',
  forecastFinish: task.forecastFinish ?? '',
  actualStart: task.actualStart ?? '',
  actualFinish: task.actualFinish ?? '',
  percentComplete: task.percentComplete,
  employeeId: task.employeeId ?? '',
  remarks: task.remarks ?? '',
})

const STATE_WORDS: Readonly<Record<ScheduleTask['state'], string>> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'Under way',
  DONE: 'Done',
}

/** `null` when there is nothing to compare; a signed day count otherwise. */
const slipText = (days: number | null): string => {
  if (days === null) return '—'
  if (days === 0) return 'On plan'
  return days > 0 ? `${days} day${days === 1 ? '' : 's'} late` : `${-days} day${days === -1 ? '' : 's'} early`
}

export const SchedulePage = ({ projectId }: { projectId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  const key = ['schedule', api.companyId, projectId]
  const schedule = useQuery({
    queryKey: key,
    queryFn: () => api.schedule(projectId),
    enabled: projectId !== '',
  })

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('Nothing to save.')
      const orNull = (v: string): string | null => (v === '' ? null : v)
      return api.saveScheduleTask({
        ...(draft.id ? { id: draft.id } : {}),
        projectId,
        name: draft.name,
        wbs: orNull(draft.wbs),
        plannedStart: orNull(draft.plannedStart),
        plannedFinish: orNull(draft.plannedFinish),
        forecastStart: orNull(draft.forecastStart),
        forecastFinish: orNull(draft.forecastFinish),
        actualStart: orNull(draft.actualStart),
        actualFinish: orNull(draft.actualFinish),
        percentComplete: Number(draft.percentComplete) || 0,
        employeeId: orNull(draft.employeeId),
        remarks: orNull(draft.remarks),
      })
    },
    onSuccess: (view) => { setError(null); setDraft(null); client.setQueryData(key, view) },
    onError: (err) => setError(messageFor(err)),
  })

  const seed = useMutation({
    mutationFn: () => api.seedSchedule(projectId),
    onSuccess: (view) => { setError(null); client.setQueryData(key, view) },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.removeScheduleTask(projectId, id),
    onSuccess: (view) => { setError(null); client.setQueryData(key, view) },
    onError: (err) => setError(messageFor(err)),
  })

  if (projectId === '') {
    return <Empty title="No project chosen" body={['Pick a project above to see its schedule.']} />
  }
  if (schedule.isLoading) return <Loading />
  if (schedule.error) return <ErrorBlock error={schedule.error} />

  const summary = schedule.data?.summary
  const tasks = schedule.data?.tasks ?? []

  const columns: ReadonlyArray<ColumnDef<ScheduleTask>> = [
    { key: 'wbs', header: 'Ref', width: 100, render: (r) => r.wbs ?? '—' },
    {
      key: 'name',
      header: 'Task',
      render: (r) => (
        <button type="button" className="linkish" onClick={() => setDraft(draftOf(r))}>{r.name}</button>
      ),
    },
    { key: 'plannedStart', header: 'Planned from', width: 140, render: (r) => formatDate(r.plannedStart) },
    { key: 'plannedFinish', header: 'Planned to', width: 140, render: (r) => formatDate(r.plannedFinish) },
    { key: 'forecastFinish', header: 'Forecast', width: 140, render: (r) => formatDate(r.forecastFinish) },
    { key: 'actualFinish', header: 'Actual', width: 140, render: (r) => formatDate(r.actualFinish) },
    {
      key: 'percentComplete',
      header: 'Done',
      align: 'right',
      width: 90,
      render: (r) => `${Number(r.percentComplete).toFixed(0)}%`,
    },
    { key: 'state', header: 'State', width: 130, render: (r) => STATE_WORDS[r.state] },
    {
      key: 'slipDays',
      header: 'Against plan',
      width: 160,
      render: (r) => (
        <span className={r.overdue ? 'over' : undefined}>{slipText(r.slipDays)}</span>
      ),
    },
    { key: 'employeeName', header: 'With', width: 180, render: (r) => r.employeeName ?? '—' },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (r) => <Button kind="ghost" onClick={() => remove.mutate(r.id)}>Remove</Button>,
    },
  ]

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      {summary && summary.taskCount > 0 ? (
        <Panel title="Where this job stands">
          <div className="row row--wrap" style={{ gap: 40 }}>
            <div>
              <div className="label">Complete</div>
              <div className="figure figure--small">{Number(summary.percentComplete).toFixed(0)}%</div>
            </div>
            <div>
              <div className="label">Tasks</div>
              <div className="figure figure--small">{summary.done} of {summary.taskCount} done</div>
            </div>
            <div>
              <div className="label">Past their date</div>
              <div className={summary.overdue > 0 ? 'figure figure--small over' : 'figure figure--small'}>
                {summary.overdue}
              </div>
            </div>
            <div>
              <div className="label">Planned finish</div>
              <div className="figure figure--small">{formatDate(summary.plannedFinish)}</div>
            </div>
            <div>
              <div className="label">Forecast finish</div>
              <div className="figure figure--small">{formatDate(summary.forecastFinish)}</div>
            </div>
            <div>
              <div className="label">Against plan</div>
              <div className={(summary.slipDays ?? 0) > 0 ? 'figure figure--small over' : 'figure figure--small'}>
                {slipText(summary.slipDays)}
              </div>
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel
        title="The schedule"
        aside={
          <div className="row">
            {tasks.length === 0 ? (
              <Button kind="ghost" disabled={seed.isPending} onClick={() => seed.mutate()}>
                Build it from the BOQ
              </Button>
            ) : null}
            <Button kind="ghost" onClick={() => setImporting(true)}>Import from Excel</Button>
            <Button kind="primary" onClick={() => setDraft(blank())}>Add a task</Button>
          </div>
        }
      >
        <Table
          columns={columns}
          rows={tasks}
          getKey={(r) => r.id}
          empty={
            <Empty
              title="No schedule yet"
              body={[
                'Build one from the BOQ section headings — somebody already did that thinking when they priced the job.',
                'Or import the programme from Excel — our template, or an MS Project "Export to Excel" sheet as it is.',
                'Or add tasks by hand.',
              ]}
            />
          }
        />
      </Panel>

      {importing ? (
        <SheetImportDialog
          title="Import the schedule from Excel"
          intro={<>
            Download the template — it comes filled in with the tasks already here — change it
            and upload it. A row updates the task with the same WBS, else the one with the same
            name, else adds a task. An MS Project export (Task Name, Outline Level, Start, Finish,
            % Complete) can be uploaded as it is; its outline levels build the hierarchy. Nothing
            is deleted.
          </>}
          templateUrl={api.scheduleTemplateUrl(projectId)}
          validate={(base64, fileName) => api.validateScheduleImport(projectId, base64, fileName)}
          apply={(base64, fileName) => api.applyScheduleImport(projectId, base64, fileName)}
          onApplied={() => { void client.invalidateQueries({ queryKey: key }) }}
          onClose={() => setImporting(false)}
        />
      ) : null}

      {draft ? (
        <Modal
          title={draft.id ? 'Change this task' : 'Add a task'}
          subtitle="Planned is what was agreed. Forecast is what is now expected. Actual is what happened."
          wide
          actions={
            <>
              <Button kind="ghost" onClick={() => setDraft(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={draft.name.trim() === '' || save.isPending}
                onClick={() => save.mutate()}
              >
                Save
              </Button>
            </>
          }
          onClose={() => setDraft(null)}
        >
          <div className="form-grid">
            <Field label="Task" required>
              <Input value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
            </Field>
            <Field label="Reference" hint="The BOQ section reference, if it has one.">
              <Input value={draft.wbs} onChange={(wbs) => setDraft({ ...draft, wbs })} />
            </Field>

            <Field label="Planned from">
              <Input type="date" value={draft.plannedStart} onChange={(v) => setDraft({ ...draft, plannedStart: v })} />
            </Field>
            <Field label="Planned to">
              <Input type="date" value={draft.plannedFinish} onChange={(v) => setDraft({ ...draft, plannedFinish: v })} />
            </Field>

            <Field label="Forecast from" hint="Left empty it follows the plan.">
              <Input type="date" value={draft.forecastStart} onChange={(v) => setDraft({ ...draft, forecastStart: v })} />
            </Field>
            <Field label="Forecast to" hint="Change this the moment you know the plan will not hold.">
              <Input type="date" value={draft.forecastFinish} onChange={(v) => setDraft({ ...draft, forecastFinish: v })} />
            </Field>

            <Field label="Actually started">
              <Input type="date" value={draft.actualStart} onChange={(v) => setDraft({ ...draft, actualStart: v })} />
            </Field>
            <Field label="Actually finished" hint="Setting this marks the task complete.">
              <Input type="date" value={draft.actualFinish} onChange={(v) => setDraft({ ...draft, actualFinish: v })} />
            </Field>

            <Field label="Per cent complete">
              <Input
                align="right"
                value={draft.percentComplete}
                onChange={(v) => setDraft({ ...draft, percentComplete: v })}
              />
            </Field>
            <Field label="With">
              <Lookup
                entity="employee"
                value={draft.employeeId}
                onChange={(employeeId) => setDraft({ ...draft, employeeId })}
              />
            </Field>
          </div>
          <Field label="Remarks">
            <textarea
              className="control"
              style={{ minHeight: 90 }}
              value={draft.remarks}
              onChange={(event) => setDraft({ ...draft, remarks: event.currentTarget.value })}
            />
          </Field>
        </Modal>
      ) : null}
    </>
  )
}
